import { RubyLoader } from '../../components/RubyLoader';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingCart, AlertTriangle, Plus, Package, Archive, RotateCcw, Pencil, Copy } from 'lucide-react';
import ShimmerImage from '../../components/ShimmerImage';
import { hardwareApi, money, type DeviceBundleRow, type DeviceModelRow } from '../../services/hardwareApi';
import { billingApi, type BillingSkuRow } from '../../services/billingApi';
import documentsApi from '../../services/documentsApi';
import { InventoryPage, InventorySubtabs } from './InventoryChrome';
import {
  quoteKit, fallbackNote, MODE_ICON, MODE_STYLE,
  type FinancePart, type ModeQuote,
} from './bundleFinance';
import DealsPage from '../DealsPage';

/**
 * SALES — Bundles and Deals are each their own SIDE-NAV page now (Layout.tsx's
 * "Sales" group, next to Orders), mirroring the Inventory hub's
 * route-per-section pattern: `tab` (route mode) says which section this route
 * renders — no in-page tab bar, the sidebar IS the navigation. Deals is the
 * EXISTING `DealsPage` list rendered unchanged so there is exactly one deals
 * list in the app. Legacy `/inventory/sales?tab=…` (and `/deals`) redirect to
 * the section's own route so nothing that already links to them breaks.
 */

const SALE_MODE_LABEL: Record<string, string> = {
  outright: 'BUY', installment: 'FINANCE', lease: 'LEASE', rental: 'RENTAL', included: 'FREE',
};
const SALE_MODE_CHIP: Record<string, string> = {
  outright: 'bg-blue-100 text-blue-700', installment: 'bg-rose-100 text-rose-700',
  lease: 'bg-amber-100 text-amber-700', rental: 'bg-emerald-100 text-emerald-700',
  included: 'bg-gray-100 text-gray-600',
};

function offerPriceLabel(sku: Pick<BillingSkuRow, 'saleMode' | 'amountCents' | 'interval' | 'termMonths'>): string {
  const amt = money(sku.amountCents);
  switch (sku.saleMode) {
    case 'outright': return `${amt} once`;
    case 'included': return 'Included free';
    case 'installment': return `${amt}/${sku.interval === 'year' ? 'yr' : 'mo'} × ${sku.termMonths ?? '?'}`;
    case 'lease': return `${amt}/${sku.interval === 'year' ? 'yr' : 'mo'} lease`;
    case 'rental': return `${amt}/${sku.interval === 'year' ? 'yr' : 'mo'}, no term`;
    default: return amt;
  }
}

/**
 * The reseller/retail tier line under an offer, when the wizard set it (both
 * are optional — an offer created through the old bare Offers-tab editor has
 * neither, and shows nothing here rather than a fabricated tier).
 */
function tierLabel(sku: BillingSkuRow): string | null {
  if (sku.saleMode === 'outright' && sku.resellerPriceCents) {
    return `reseller ${money(sku.resellerPriceCents)} · retail ${money(sku.amountCents)}`;
  }
  if (sku.saleMode === 'installment' && sku.resellerMonthlyCents) {
    return `reseller ${money(sku.resellerMonthlyCents)}/mo · retail ${money(sku.amountCents)}/mo`;
  }
  return null;
}

/**
 * ARCHIVING A BUNDLE.
 *
 * A kit is never deleted — the deals already sold against it still have to
 * resolve its name, photo and parts list forever. Archiving is `status = 'eol'`
 * on the bundle row: the kit stops being offered for sale and disappears from
 * every picker that starts NEW work (deal editor, financing wizard, commission
 * targets), while existing deals keep exactly the hardware they were sold with.
 * Restoring puts it back to `active`.
 */
const isArchived = (b: DeviceBundleRow) => b.status === 'eol';

/**
 * THE KIT'S PHOTOS, ONE AT A TIME.
 *
 * A bundle is several devices in a box, and the card was showing one thumbnail
 * of one of them — so two kits built around the same terminal looked identical.
 * The photos cycle slowly instead: each drifts up into place as the one before
 * it fades, which reads as a kit being shown off rather than an image swapping.
 *
 * A single photo does not animate at all, and neither does anything when the
 * viewer has asked for reduced motion — an image that moves on its own is
 * exactly what that setting is asking us not to do.
 */
const BundleGallery: React.FC<{ photos: string[]; alt: string; size?: number }> = ({ photos, alt, size = 72 }) => {
  const [i, setI] = useState(0);
  const [animate, setAnimate] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (mq?.matches) { setAnimate(false); return undefined; }
    if (photos.length < 2) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % photos.length), 3800);
    return () => clearInterval(t);
  }, [photos.length]);

  // Keep the index inside the list when a bundle is edited under us.
  const active = photos.length ? i % photos.length : 0;

  return (
    <div
      className="relative shrink-0 overflow-hidden rounded-xl bg-stone-900"
      style={{ width: size, height: size }}
    >
      {photos.length === 0 ? (
        <div className="flex h-full w-full items-center justify-center">
          <Package size={22} className="text-stone-500" />
        </div>
      ) : photos.map((src, n) => (
        <div
          key={`${src}-${n}`}
          className={`absolute inset-0 transition-all duration-700 ease-out ${
            n === active ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
          }`}
          aria-hidden={n !== active}
        >
          <ShimmerImage
            src={src}
            alt={n === active ? alt : ''}
            width={size}
            height={size}
            objectFit="contain"
            className="rounded-xl"
            fallbackBgColor="bg-stone-900"
            fallbackIcon={<Package size={20} className="text-stone-400" />}
            variant="dark"
          />
        </div>
      ))}
      {/* How many pictures there are, and which one this is — without it a
          card that changes on its own looks like a glitch. */}
      {photos.length > 1 && animate && (
        <div className="absolute inset-x-0 bottom-1 flex justify-center gap-1">
          {photos.map((_, n) => (
            <span
              key={n}
              className={`h-1 w-1 rounded-full transition-colors ${n === active ? 'bg-white' : 'bg-white/35'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * WHAT A KIT INHERITS FROM ITS DEVICES. The arithmetic — including how a
 * rental-only part is handled when the kit is quoted outright — lives in
 * `bundleFinance`, shared with the bundle builder so the two cannot drift.
 */
const inheritedFinance = (
  b: DeviceBundleRow,
  skus: BillingSkuRow[],
  nameOf: (modelId: string) => string,
) => {
  const parts: FinancePart[] = b.components.map((c) => ({
    modelId: c.modelId,
    name: nameOf(c.modelId),
    quantity: c.quantity,
    offers: skus.filter((s) => s.modelId === c.modelId),
  }));
  return {
    quotes: quoteKit(parts),
    partCount: parts.length,
    partsWithoutTerms: parts.filter((p) => p.offers.length === 0).map((p) => p.name),
  };
};

/** The label a quote line carries: what this device is charged as. */
const LINE_MODE_LABEL: Record<string, string> = {
  outright: 'Outright', installment: 'Installment', lease: 'Lease',
  rental: 'Rental', included: 'Included free', none: 'No terms',
};

/**
 * Archive/restore confirmation. A custom dialog, never `window.confirm` — the
 * admin runs inside an iOS webview where native confirms are unreliable.
 */
const ArchiveConfirm: React.FC<{
  bundle: DeviceBundleRow;
  offerCount: number;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ bundle, offerCount, busy, onCancel, onConfirm }) => {
  const archiving = !isArchived(bundle);
  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" onClick={() => !busy && onCancel()} />
      <div className="relative w-full max-w-md rounded-2xl border border-stone-200 bg-white p-5 shadow-2xl">
        <div className="flex items-center gap-2.5">
          {archiving ? <Archive size={18} className="text-amber-600" /> : <RotateCcw size={18} className="text-emerald-600" />}
          <h3 className="text-base font-bold text-stone-900">
            {archiving ? 'Archive this bundle?' : 'Restore this bundle?'}
          </h3>
        </div>
        <p className="mt-3 text-sm text-stone-600">{bundle.name}</p>
        {archiving ? (
          <ul className="mt-3 space-y-1.5 text-[12px] text-stone-600">
            <li>· It stops being offered for sale and moves to the Archived tab.</li>
            <li>· It can no longer be added to a deal, a financing policy or a commission rule.</li>
            <li>· Deals that already include it are <strong>not</strong> changed — they keep billing exactly as they are.</li>
            {offerCount > 0 && (
              <li>· Its {offerCount} finance option{offerCount === 1 ? '' : 's'} stay attached, so old deals still price correctly.</li>
            )}
            <li>· You can restore it at any time.</li>
          </ul>
        ) : (
          <p className="mt-3 text-[12px] text-stone-600">
            It becomes sellable again and reappears in the deal, financing and commission pickers.
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="min-h-[44px] rounded-lg border border-stone-300 px-4 text-sm font-medium text-stone-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="min-h-[44px] rounded-lg px-4 text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: archiving ? '#B45309' : '#047857' }}
          >
            {busy ? 'Saving…' : archiving ? 'Archive bundle' : 'Restore bundle'}
          </button>
        </div>
      </div>
    </div>
  );
};

const BundlesTab: React.FC<{ view: 'active' | 'archived' }> = ({ view }) => {
  const navigate = useNavigate();
  const [bundles, setBundles] = useState<DeviceBundleRow[]>([]);
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [skus, setSkus] = useState<BillingSkuRow[]>([]);
  const [agreementTitles, setAgreementTitles] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** The bundle whose archive/restore is awaiting confirmation. */
  const [pending, setPending] = useState<DeviceBundleRow | null>(null);
  /** Which kit's which quote is open. One at a time — this is a list of cards. */
  /**
   * Which way of selling each kit is being shown. A kit ALWAYS shows one —
   * outright by default, or the first mode it can be sold on — because the
   * breakdown below is the only place the totals are stated, and a card that
   * starts collapsed states no price at all.
   */
  const [chosenMode, setChosenMode] = useState<Record<string, ModeQuote['mode']>>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, m, s] = await Promise.all([
        hardwareApi.listBundles(),
        hardwareApi.listModels(),
        billingApi.listSkus().catch(() => ({ items: [] as BillingSkuRow[] })),
      ]);
      setBundles(b.items || []);
      setModels(m.items || []);
      setSkus((s.items || []).filter((x) => x.type === 'device_finance' && x.status === 'active'));
    } catch (e: any) {
      setError(e?.message || 'Failed to load bundles');
    } finally {
      setLoading(false);
    }
    // Agreement names for the offer strip — best-effort, non-blocking. A
    // documents-service outage must not stop bundles/offers from rendering.
    try {
      const docsRes = await documentsApi.listDocuments({ status: 'active' });
      setAgreementTitles(new Map((docsRes.documents || []).map((d) => [d.documentId, d.title])));
    } catch {
      setAgreementTitles(new Map());
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const modelById = useMemo(() => new Map(models.map((m) => [m.modelId, m])), [models]);

  // One list per tab. Archived kits never mix into the sellable list.
  const visible = useMemo(
    () => bundles.filter((b) => (view === 'archived' ? isArchived(b) : !isArchived(b))),
    [bundles, view],
  );

  const confirmStatus = async () => {
    if (!pending) return;
    setSaving(true);
    setError(null);
    try {
      await hardwareApi.setBundleStatus(pending.bundleId, isArchived(pending) ? 'active' : 'eol');
      setPending(null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not change the bundle status');
      setPending(null);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <RubyLoader variant="page" />;
  }

  return (
    <div>
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-600" />
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}
      {view === 'active' && (
        <div className="flex justify-end mb-4">
          <button
            // Straight into the builder — pressing New bundle must not land on
            // a page whose only offer is the same button again.
            onClick={() => navigate('/inventory/bundles-editor/new')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium min-h-[44px]"
            style={{ background: '#DC143C' }}
          >
            <Plus size={16} /> New bundle
          </button>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-white py-16 text-center">
          <Package size={28} className="mx-auto mb-3 text-stone-300" />
          <p className="text-sm font-medium text-stone-700">
            {view === 'archived' ? 'Nothing archived' : 'No bundles yet'}
          </p>
          {view === 'archived' && (
            <p className="mt-1 text-xs text-stone-500">
              Archived bundles stay here so old deals keep resolving — they are never deleted.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {visible.map((b) => {
            const archived = isArchived(b);
            const offers = skus.filter((s) => s.bundleId === b.bundleId);
            /**
             * FINANCE COMES FROM THE PARTS. Selling terms are written against a
             * DEVICE — that is where the wizard puts them and where the
             * agreement hangs — so a kit is sellable when its devices are. This
             * card used to look only for bundle-level SKUs and declared every
             * kit "can't be sold yet", which was never true of a kit built from
             * devices that all had terms.
             */
            const inherited = inheritedFinance(b, skus, (id) => modelById.get(id)?.modelName || id);
            const componentNames = b.components.map((c) => {
              const m = modelById.get(c.modelId);
              return `${m?.modelName || c.modelId}${c.quantity > 1 ? ` ×${c.quantity}` : ''}`;
            });
            return (
              <div
                key={b.bundleId}
                className={`rounded-xl border p-5 ${
                  archived ? 'border-stone-200 bg-stone-50' : 'border-stone-200 bg-white'
                }`}
              >
                {/* THE PICTURE LEADS. Everything else — name, parts, status,
                    the two actions — arranges itself in the column beside it
                    rather than competing with it for the same row. */}
                <div className="flex items-start gap-4">
                  {/* Hero first, then the rest of the gallery — deduped, so a
                      hero that is also in `photos` does not show twice. */}
                  <BundleGallery
                    alt={b.name}
                    size={112}
                    photos={[...new Set([b.heroImageUrl, ...(b.photos || [])].filter(Boolean) as string[])]}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[15px] font-bold text-stone-900">{b.name}</span>
                      <span className={`shrink-0 text-[10px] font-semibold rounded-full px-2.5 py-1 border ${
                        archived ? 'bg-stone-200 text-stone-600 border-stone-300'
                          : (offers.length > 0 || inherited.quotes.length > 0) ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-amber-100 text-amber-700 border-amber-200'
                      }`}>
                        {archived ? 'Archived'
                          : (offers.length > 0 || inherited.quotes.length > 0) ? 'Sellable' : 'No terms'}
                      </span>
                    </div>
                    <div className="mt-1 text-[11.5px] leading-relaxed text-stone-500">
                      {componentNames.join(' · ')}
                    </div>
                    <div className="mt-1 text-[11px] text-stone-400">
                      {b.components.length} part{b.components.length === 1 ? '' : 's'} · costs us{' '}
                      <span className="font-mono text-stone-600">{money(b.totalCostCents)}</span>
                    </div>
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {/* EDIT — a kit was creatable and then unreachable: this
                          list could archive one and nothing could open it. */}
                      {!archived && (
                        <button
                          onClick={() => navigate(`/inventory/bundles-editor/${encodeURIComponent(b.bundleId)}`)}
                          title="Open this kit in the bundle builder"
                          className="flex items-center gap-1.5 rounded-lg border border-stone-300 px-3 py-2 text-[11.5px] font-semibold text-stone-600 min-h-[36px] hover:border-stone-400 hover:text-stone-900"
                        >
                          <Pencil size={13} /> Edit
                        </button>
                      )}
                      {/* COPY — most kits are a near-neighbour of one that
                          already exists (same terminal, one printer swapped),
                          and rebuilding that part list by hand is where the
                          mistakes come from. Opens the builder pre-filled on
                          the NEW route: the copy does not exist until it is
                          saved, and this kit is only read. Offered on archived
                          kits too — a retired kit is often exactly the thing
                          worth basing its replacement on. */}
                      <button
                        onClick={() => navigate(`/inventory/bundles-editor/new?copyFrom=${encodeURIComponent(b.bundleId)}`)}
                        title="Start a new kit from a copy of this one"
                        className="flex items-center gap-1.5 rounded-lg border border-stone-300 px-3 py-2 text-[11.5px] font-semibold text-stone-600 min-h-[36px] hover:border-stone-400 hover:text-stone-900"
                      >
                        <Copy size={13} /> Copy
                      </button>
                      <button
                        onClick={() => setPending(b)}
                        title={archived ? 'Restore this bundle' : 'Archive this bundle'}
                        className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-[11.5px] font-semibold min-h-[36px] ${
                          archived
                            ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                            : 'border-stone-300 text-stone-500 hover:border-amber-300 hover:text-amber-700'
                        }`}
                      >
                        {archived ? <RotateCcw size={13} /> : <Archive size={13} />}
                        {archived ? 'Restore' : 'Archive'}
                      </button>
                    </div>
                  </div>
                </div>

                {archived && (
                  <p className="mt-3 rounded-lg bg-stone-100 px-3 py-2 text-[11px] text-stone-600">
                    Not offered for sale. It can't be added to a new deal — deals that already include it are unchanged.
                  </p>
                )}

                <div className="mt-3.5 pt-3 border-t border-dashed border-stone-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-stone-500">How it sells</span>
                    <span className={`text-[11px] font-semibold ${
                      offers.length > 0 ? 'text-red-500'
                        : inherited.quotes.length > 0 ? 'text-stone-500'
                          : 'text-amber-600'
                    }`}>
                      {offers.length > 0
                        ? `${offers.length} offer${offers.length === 1 ? '' : 's'} on the kit`
                        : inherited.quotes.length > 0 ? 'inherited from the devices'
                          : '⚠ no device carries terms yet'}
                    </span>
                  </div>

                  {/* FROM THE DEVICES — one quote per way the kit can be sold,
                      each opening into the device-by-device breakdown behind
                      it. Terms are never set here; they are set on the device,
                      which is what the breakdown names. */}
                  {inherited.quotes.length > 0 && (() => {
                    /* ONE ROW OF WAYS TO SELL, one of them always chosen —
                       outright first, since that is how most kits are quoted.
                       The tiles carry the mode and nothing else: the price
                       belongs to the breakdown below, where it is stated once,
                       in full, instead of twice in two sizes. */
                    const picked = chosenMode[b.bundleId]
                      || (inherited.quotes.find((x) => x.mode === 'outright') || inherited.quotes[0]).mode;
                    const q = inherited.quotes.find((x) => x.mode === picked) || inherited.quotes[0];
                    return (
                    <div className="mb-2 space-y-2">
                      <div className="-mx-0.5 flex gap-2 overflow-x-auto px-0.5 pb-1">
                        {inherited.quotes.map((qq) => {
                          const on = qq.mode === q.mode;
                          const style = MODE_STYLE[qq.mode];
                          const Icon = MODE_ICON[qq.mode];
                          const mixed = qq.fallbacks.length > 0 || qq.missing.length > 0;
                          return (
                            <button
                              key={qq.mode}
                              onClick={() => setChosenMode((prev) => ({ ...prev, [b.bundleId]: qq.mode }))}
                              title="See what each device is charged"
                              className={`flex shrink-0 items-center gap-2 rounded-xl border px-3 py-2 transition ${
                                on ? style.open : style.idle
                              }`}
                            >
                              <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg ${style.badge}`}>
                                <Icon size={15} />
                              </span>
                              <span className={`text-[12px] font-bold capitalize ${on ? style.label : 'text-stone-600'}`}>
                                {qq.mode}
                              </span>
                              {mixed && <AlertTriangle size={11} className="shrink-0 text-amber-500" />}
                            </button>
                          );
                        })}
                      </div>

                      {/* THE BREAKDOWN — device by device, with the picture,
                          because "$1,050 upfront + $14.99/mo" is only useful
                          once you can see WHICH part is the monthly one. */}
                      {(() => {
                        return (
                          <div className="rounded-xl border border-stone-200 bg-stone-50/70 p-2.5">
                            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-stone-500">
                              Sold as {q.mode} — device by device
                            </div>
                            <div className="space-y-1">
                              {q.lines.map((ln) => {
                                const m = modelById.get(ln.modelId);
                                const odd = ln.usedMode !== q.mode;
                                return (
                                  <div key={ln.modelId} className="flex items-center gap-2 rounded-lg bg-white px-2 py-1.5">
                                    <ShimmerImage
                                      src={m?.media?.heroImageUrl}
                                      alt={ln.name}
                                      width={28}
                                      height={28}
                                      objectFit="contain"
                                      className="shrink-0 rounded-md"
                                      fallbackBgColor="bg-stone-100"
                                      fallbackIcon={<Package size={13} className="text-stone-400" />}
                                    />
                                    <span className="min-w-0 flex-1">
                                      <span className="block truncate text-[12px] font-semibold text-stone-800">
                                        {ln.name}
                                        {ln.quantity > 1 && <span className="ml-1 font-mono text-[10.5px] text-stone-400">×{ln.quantity}</span>}
                                      </span>
                                      <span className={`block text-[10px] ${odd ? 'text-amber-700' : 'text-stone-400'}`}>
                                        {LINE_MODE_LABEL[ln.usedMode]}
                                        {odd && ln.usedMode !== 'none' && ln.usedMode !== 'included' && ` — ${ln.usedMode}-only`}
                                        {ln.termMonths ? ` · ${ln.termMonths} mo` : ''}
                                      </span>
                                    </span>
                                    <span className="shrink-0 text-right font-mono text-[12px] text-stone-800">
                                      {ln.usedMode === 'none' ? '—'
                                        : ln.usedMode === 'included' ? 'free'
                                          : `${money(ln.lineCents)}${ln.recurring ? '/mo' : ''}`}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>

                            {/* THE TOTALS, AND THE ONLY PLACE THEY APPEAR.
                                Upfront money and monthly money are different
                                kinds of money, so they are stated side by side
                                and never summed. Big, because this is the
                                number somebody quotes down a phone. */}
                            <div className="mt-2.5 flex flex-wrap items-end gap-x-8 gap-y-2 border-t border-stone-200 pt-2.5">
                              <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Due upfront</div>
                                <div className="font-mono text-lg font-bold leading-tight text-stone-900">{money(q.upfrontCents)}</div>
                              </div>
                              <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Then monthly</div>
                                <div className="font-mono text-lg font-bold leading-tight text-stone-900">
                                  {money(q.monthlyCents)}<span className="text-[12px] font-semibold text-stone-500">/mo</span>
                                </div>
                              </div>
                              {fallbackNote(q) && (
                                <p className="flex-1 text-right text-[11px] text-amber-700">{fallbackNote(q)}</p>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    );
                  })()}
                  {inherited.partsWithoutTerms.length > 0 && (
                    <p className="mb-2 text-[11px] text-amber-700">
                      {inherited.partsWithoutTerms.join(', ')}
                      {' '}
                      {inherited.partsWithoutTerms.length === 1 ? 'carries' : 'carry'} no selling terms — set them on the
                      device, under Devices → Selling terms.
                    </p>
                  )}

                  {offers.length === 0 ? (
                    inherited.quotes.length === 0 && (
                      <p className="text-[11px] text-stone-500 pb-2">
                        None of the devices in this kit has selling terms, so there is nothing for a deal to price.
                      </p>
                    )
                  ) : (
                    <div className="space-y-1.5">
                      {offers.map((o) => {
                        const tier = tierLabel(o);
                        const agreementName = o.agreementTemplateId ? agreementTitles.get(o.agreementTemplateId) : undefined;
                        return (
                          <div key={o.skuId} className="py-1.5 border-t border-stone-100 first:border-t-0">
                            <div className="flex items-center gap-2 text-xs">
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${SALE_MODE_CHIP[o.saleMode || 'outright']}`}>
                                {SALE_MODE_LABEL[o.saleMode || 'outright']}
                              </span>
                              <span className="text-stone-700 truncate">{o.name}</span>
                              <span className="ml-auto font-mono text-stone-800 shrink-0">{offerPriceLabel(o)}</span>
                            </div>
                            {(tier || o.agreementTemplateId) && (
                              <div className="mt-0.5 pl-1 flex items-center gap-2 text-[10px] text-stone-400">
                                {tier && <span className="font-mono">{tier}</span>}
                                {o.agreementTemplateId && (
                                  <span>
                                    · {agreementName ? `${agreementName} — v${o.agreementTemplateVersion ?? '?'}` : `agreement v${o.agreementTemplateVersion ?? '?'}`}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* NO "add finance option" HERE. Terms belong to a device;
                      offering to attach one to the kit created a second, rival
                      place for the same fact. A kit-level offer that already
                      exists is still shown above, and still editable in the
                      wizard through the link below. */}
                  {!archived && offers.length > 0 && (
                    <button
                      onClick={() => navigate(`/inventory/financing/new?edit=bundle:${encodeURIComponent(b.bundleId)}`)}
                      className="w-full mt-2 flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-dashed border-stone-300 text-stone-500 text-xs font-semibold hover:text-red-500 hover:border-red-300 min-h-[40px]"
                    >
                      Edit the kit-level offers
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {pending && (
        <ArchiveConfirm
          bundle={pending}
          offerCount={skus.filter((s) => s.bundleId === pending.bundleId).length}
          busy={saving}
          onCancel={() => setPending(null)}
          onConfirm={confirmStatus}
        />
      )}
    </div>
  );
};

const SalesPage: React.FC<{ tab?: 'bundles' | 'deals' }> = ({ tab: tabProp }) => {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<'active' | 'archived'>('active');

  // Legacy mode (no `tab` prop): old `/inventory/sales?tab=…` links redirect
  // to the section's own route.
  if (!tabProp) {
    return <Navigate to={searchParams.get('tab') === 'deals' ? '/inventory/deals' : '/inventory/bundles'} replace />;
  }

  // Deals is a full self-contained page (its own black header, stats, list,
  // delete/toggle) — never wrap it in this page's own header too.
  if (tabProp === 'deals') return <DealsPage />;

  return (
    <InventoryPage
      icon={ShoppingCart}
      title="Bundles"
      subtitle="Device bundles — everything you sell"
      tabs={(
        <InventorySubtabs
          tabs={[{ id: 'active', label: 'Active' }, { id: 'archived', label: 'Archived' }]}
          active={view}
          onChange={(id) => setView(id as 'active' | 'archived')}
        />
      )}
    >
      <BundlesTab view={view} />
    </InventoryPage>
  );
};

export default SalesPage;
